import React, { useCallback, useEffect, useState } from 'react';
import {
    Drawer, Descriptions, Tag, Table, Button, Space, Typography, Divider,
    message, Spin, Alert, Popconfirm, Tooltip,
} from 'antd';
import {
    ThunderboltOutlined, MailOutlined, CopyOutlined, ReloadOutlined, StopOutlined,
} from '@ant-design/icons';
import TelnaApiService from '../../APIServices/TelnaApiService';
import {
    formatBytes, formatDate, formatMoney,
    ORDER_STATUS_COLOR, ITEM_STATUS_COLOR, PACKAGE_STATUS_COLOR,
} from './telnaHelpers';

const { Text, Paragraph } = Typography;

/**
 * One Telna order, in full.
 *
 * The QR is an <img> on a `data:` URL the BACKEND rendered. It is deliberately
 * not drawn from a public QR-generator URL: the activation code is the eSIM,
 * and anyone who sees that string can install the profile onto their own device
 * with no way for us to invalidate it. It never leaves our infrastructure.
 *
 * Remaining data comes from `livePackages`, fetched fresh per open rather than
 * stored — a customer calling about "my data ran out" needs the number now, not
 * the number at provisioning time.
 */
const TelnaOrderDrawer = ({ open, orderNumber, onClose, onChanged, provisioningEnabled }) => {
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        if (!orderNumber) return;
        setLoading(true);
        try {
            const res = await TelnaApiService.getOrder(orderNumber);
            setOrder(res.order);
        } catch (err) {
            message.error(err.message);
            setOrder(null);
        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => { if (open && orderNumber) load(); }, [open, orderNumber, load]);

    const copy = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value);
            message.success(`${label} copied`);
        } catch {
            // Clipboard is refused outside a secure context; show it rather
            // than failing silently.
            message.info(value);
        }
    };

    const act = async (key, fn, successText) => {
        setBusy(key);
        try {
            await fn();
            message.success(successText);
            await load();
            onChanged?.();
        } catch (err) {
            // The backend distinguishes "switch is off" from "Telna is down",
            // and so must the message — one is a setting, the other an outage.
            if (err.code === 'TELNA_PROVISIONING_DISABLED') {
                message.warning('Provisioning is switched off. Set TELNA_PROVISIONING_ENABLED=true on the server to allow purchases.');
            } else {
                message.error(err.message);
            }
        } finally {
            setBusy(null);
        }
    };

    const itemColumns = [
        {
            title: '#',
            key: 'idx',
            width: 50,
            render: (_, __, i) => i + 1,
        },
        {
            title: 'Plan',
            dataIndex: 'template_name',
            render: (name, r) => (
                <div>
                    <div>{name || '—'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatBytes(r.data_allowance_bytes)} · {r.validity_days ? `${r.validity_days} days` : '—'} · {r.activation_type || '—'}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (s, r) => (
                <Tooltip title={r.error || undefined}>
                    <Tag color={ITEM_STATUS_COLOR[s] || 'default'}>{s}</Tag>
                </Tooltip>
            ),
        },
        {
            title: 'ICCID',
            dataIndex: 'iccid',
            render: (iccid) => (iccid
                ? <Space size={4}><Text code style={{ fontSize: 11 }}>{iccid}</Text>
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(iccid, 'ICCID')} /></Space>
                : <Text type="secondary">not assigned</Text>),
        },
        {
            title: 'Data left',
            key: 'left',
            width: 130,
            render: (_, r) => {
                const live = (r.livePackages || [])[0];
                if (!live) return <Text type="secondary">—</Text>;
                return (
                    <div>
                        <div>{formatBytes(live.dataRemainingBytes)}</div>
                        <Tag color={PACKAGE_STATUS_COLOR[live.status] || 'default'} style={{ marginTop: 2 }}>{live.status}</Tag>
                    </div>
                );
            },
        },
        {
            title: '',
            key: 'action',
            width: 90,
            render: (_, r, i) => (r.status === 'failed' ? (
                <Button size="small" icon={<ReloadOutlined />} loading={busy === `retry-${i}`}
                    onClick={() => act(`retry-${i}`, () => TelnaApiService.retryOrderItem(orderNumber, i), 'Line reset — provision again to retry')}>
                    Reset
                </Button>
            ) : null),
        },
    ];

    const delivered = (order?.items || []).filter((i) => i.status === 'completed' && i.activation_code);
    const hasDelivered = delivered.length > 0;

    return (
        <Drawer
            title={order ? `Order ${order.order_number}` : 'Order'}
            width={820}
            open={open}
            onClose={onClose}
            extra={order && (
                <Space>
                    <Tooltip title={provisioningEnabled ? 'Buys the eSIMs — spends wallet balance' : 'Provisioning is switched off on the server'}>
                        <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={busy === 'provision'}
                            disabled={order.status === 'cancelled' || order.status === 'completed'}
                            onClick={() => act('provision', () => TelnaApiService.provisionOrder(orderNumber), 'Provisioning finished')}
                        >
                            Provision
                        </Button>
                    </Tooltip>
                    <Button
                        icon={<MailOutlined />}
                        loading={busy === 'email'}
                        disabled={!hasDelivered}
                        onClick={() => act('email', () => TelnaApiService.sendOrderEmail(orderNumber), 'Email sent')}
                    >
                        {order.email_sent ? 'Resend email' : 'Send email'}
                    </Button>
                    <Popconfirm
                        title="Cancel this order?"
                        description="Reserved ICCIDs go back into the pool. Refused if any eSIM was already delivered."
                        onConfirm={() => act('cancel', () => TelnaApiService.cancelOrder(orderNumber), 'Order cancelled')}
                        okText="Cancel order"
                        cancelText="Keep"
                    >
                        <Button danger icon={<StopOutlined />} loading={busy === 'cancel'}
                            disabled={order.status === 'cancelled' || hasDelivered}>
                            Cancel
                        </Button>
                    </Popconfirm>
                </Space>
            )}
        >
            {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

            {!loading && order && (
                <>
                    {!provisioningEnabled && order.status !== 'completed' && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Provisioning is switched off"
                            description="The server will refuse to buy eSIMs until TELNA_PROVISIONING_ENABLED=true. Everything else on this page works."
                        />
                    )}

                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Status">
                            <Tag color={ORDER_STATUS_COLOR[order.status] || 'default'}>{order.status}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Source">{order.source}</Descriptions.Item>
                        <Descriptions.Item label="Customer">{order.customer_name || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Email">{order.customer_email || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Price">{formatMoney(order.price, order.currency)}</Descriptions.Item>
                        <Descriptions.Item label="Email sent">
                            {order.email_sent ? <Tag color="green">{formatDate(order.email_sent_at)}</Tag> : <Tag>No</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created">{formatDate(order.createdAt)}</Descriptions.Item>
                        <Descriptions.Item label="Payment ref">{order.payment_reference || '—'}</Descriptions.Item>
                        {order.notes && <Descriptions.Item label="Notes" span={2}>{order.notes}</Descriptions.Item>}
                        {order.provision_request_id && (
                            <Descriptions.Item label="Telna Request-ID" span={2}>
                                {/* The only handle Telna support will ask for. */}
                                <Text code style={{ fontSize: 11 }}>{order.provision_request_id}</Text>
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    <Divider orientation="left">Lines</Divider>
                    <Table
                        rowKey={(r, i) => i}
                        size="small"
                        pagination={false}
                        columns={itemColumns}
                        dataSource={order.items || []}
                    />

                    {hasDelivered && (
                        <>
                            <Divider orientation="left">eSIM activation</Divider>
                            {delivered.map((item, i) => (
                                <div key={item.iccid || i} style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
                                    {item.qrDataUrl && (
                                        <img
                                            src={item.qrDataUrl}
                                            alt={`eSIM QR ${i + 1}`}
                                            style={{ width: 180, height: 180, border: '1px solid #eee', borderRadius: 8 }}
                                        />
                                    )}
                                    <div style={{ flex: 1, minWidth: 260 }}>
                                        <Paragraph style={{ marginBottom: 6 }}>
                                            <Text strong>SM-DP+ address</Text><br />
                                            <Text code>{item.smdp_address || '—'}</Text>
                                        </Paragraph>
                                        <Paragraph style={{ marginBottom: 6 }}>
                                            <Text strong>Activation code</Text><br />
                                            <Text code style={{ wordBreak: 'break-all' }}>{item.matching_id || '—'}</Text>
                                        </Paragraph>
                                        <Paragraph style={{ marginBottom: 6 }}>
                                            <Text strong>Full LPA string</Text><br />
                                            <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{item.activation_code}</Text>
                                        </Paragraph>
                                        <Button size="small" icon={<CopyOutlined />} onClick={() => copy(item.activation_code, 'Activation string')}>
                                            Copy activation string
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </>
            )}
        </Drawer>
    );
};

export default TelnaOrderDrawer;
